import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { OverlayContext } from 'services/contexts'
import { useElement } from 'services/hooks'
import usePreventScroll from 'services/hooks/usePreventScroll'

interface IHeightProp {
  height?: number
}

interface ITransitionStartedProp {
  transitionStarted: boolean
}
const Container = styled.div<IHeightProp & ITransitionStartedProp>`
  z-index: ${props => props.theme.zIndices.level8}; // 1 level higher than overlay
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: ${props => props.theme.colors.white};

  box-shadow: ${props => props.theme.shadows.neutral.level5};
  border-top-left-radius: ${props => props.theme.radii[12]}px;
  border-top-right-radius: ${props => props.theme.radii[12]}px;
  padding: ${props => props.theme.spacings[16]}px ${props => props.theme.spacings[24]}px;

  overflow-y: scroll;
  max-height: calc(100% - ${props => props.theme.spacings[24]}px);
  transform: translateY(
    ${props =>
    props.transitionStarted ? `calc((100% - ${props.theme.spacings[24]}px) * -1)` : '0px'}
  );
  bottom: ${props => `calc((100% - ${props.theme.spacings[48]}px) * -1)`};

  ${props =>
    props.height
    && `
    height: ${props.height}px;
    bottom: ${-props.height}px;
    transform: translateY(${props.transitionStarted ? -props.height : 0}px);
    overflow-y: visible;
  `}

  transition: transform 0.3s ease-out;
`

interface IProps {
  height?: number
  visible: boolean
  setVisible?: (visible: boolean) => void
  onClose: () => void
}

const MobileFilterSheet: FunctionComponent<IProps> = ({
  height,
  visible,
  setVisible,
  onClose,
  children,
}) => {
  const { showOverlay, hideOverlay, overlay } = useContext(OverlayContext)

  const [transitionStarted, setTransitionStarted] = useState(false)
  useEffect(() => {
    setTransitionStarted(visible)
  }, [visible])

  // modal and overlay logic
  const [modalCardRef, modalCardElement] = useElement<HTMLDivElement>()
  const { reenableScroll } = usePreventScroll(overlay, modalCardElement, 'modal')

  const closeModal = () => {
    /**
     * NB: Order is important here. `reenableScroll` relies on a ref, and `setVisible` can cause
     * the modal to unmount, which would clear the ref, resulting in persistent scroll lock.
     */
    reenableScroll()
    hideOverlay()
    if (onClose) onClose()

    if (setVisible) setVisible(false)
  }

  // make sure we disable the scroll lock if a parent component unmounts the modal
  useEffect(() => () => reenableScroll(), [])

  useEffect(() => {
    if (visible) {
      showOverlay()
    } else {
      hideOverlay()
    }
  }, [visible])

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    // inside click
    if (modalCardRef?.current?.contains(target)) return

    // outside click
    if (setVisible) closeModal() // sometimes modal should not be hidden on outside click
  }

  useEffect(() => {
    if (visible) document.addEventListener('mousedown', handleOutsideClick)
    else document.removeEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [visible])

  if (!visible) return null

  return (
    <Container
      height={height}
      transitionStarted={transitionStarted}
      ref={modalCardRef}
    >
      {children}
    </Container>
  )
}

export default MobileFilterSheet
